<template>
  <div class="ecm carousel-auto-settings">
    <v-checkbox
      v-model="form.autorenew"
      hide-details
      class="checkbox"
      color="#4B5EFF"
      :disabled="form.autoreplace"
      label="Автопродление аренды"
    />

    <v-checkbox
      v-model="form.autoreplace"
      hide-details
      class="checkbox"
      color="#4B5EFF"
      label="Автозамена номеров"
    />

    <template v-if="form.autoreplace">
      <div class="form-control">
        <div class="field-label">Тип автозамены</div>
        <v-radio-group v-model="form.autoReplaceType" mandatory hide-details>
          <v-radio color="#4B5EFF" value="schedule" label="По расписанию" />
          <v-radio
            color="#4B5EFF"
            value="threshold"
            label="По порогу звонков с одного номера"
          />
        </v-radio-group>
      </div>

      <template v-if="form.autoReplaceType === 'schedule'">
        <v-checkbox
          v-model="form.scheduleSeparateDays"
          hide-details
          class="separate-days-checkbox"
          color="#4B5EFF"
          label="Настроить дни отдельно"
        />

        <template v-if="!form.scheduleSeparateDays">
          <div class="form-control">
            <div class="field-label">Дни автозамены</div>
            <div class="days-input">
              <v-chip-group v-model="form.days" multiple class="days">
                <v-chip
                  v-for="day in weekdays"
                  :key="day.value"
                  :value="day.value"
                  label
                >
                  {{ day.label }}
                </v-chip>
              </v-chip-group>
            </div>
          </div>

          <div class="form-control">
            <div class="field-label">Периодичность</div>
            <v-select
              v-model="form.scheduleFrequency"
              :items="frequencyOptions"
              item-text="label"
              item-value="value"
              dense
              outlined
              hide-details
            />
          </div>

          <div v-if="form.scheduleFrequency === 'custom'" class="schedule-row">
            <v-text-field
              v-model.number="form.scheduleCustomHours"
              class="custom-period-field"
              label="Часов"
              type="number"
              min="0"
              max="12"
              outlined
              hide-details
            />
            <v-text-field
              v-model.number="form.scheduleCustomMinutes"
              class="custom-period-field"
              label="Минут"
              type="number"
              min="0"
              max="59"
              outlined
              hide-details
            />
          </div>

          <div v-if="customScheduleStepInvalid" class="error-text">
            Интервал должен быть от 10 минут до 12 часов.
          </div>

          <div class="schedule-row">
            <div class="time-col">
              <div class="sub-label">
                {{
                  form.scheduleFrequency === 'once'
                    ? 'Время автозамены'
                    : 'Первая автозамена'
                }}
              </div>

              <v-text-field
                :value="form.scheduleStartTime"
                class="time"
                dense
                outlined
                hide-details
                maxlength="5"
                placeholder="20:00"
                @input="setBaseStartTime"
                @blur="normalizeBaseStartTime"
              />
            </div>
            <div v-if="form.scheduleFrequency !== 'once'" class="time-col">
              <div class="sub-label">Последняя автозамена</div>

              <v-select
                v-model="form.scheduleEndTime"
                :items="availableEndTimes"
                outlined
                dense
                hide-details
                class="time"
                :disabled="!availableEndTimes.length"
              />
            </div>
          </div>

          <div class="schedule-preview">{{ schedulePreview }}</div>
          <div class="muted">Время по МСК</div>
        </template>

        <template v-else>
          <div class="day-schedule-list">
            <div class="day-schedule-head">
              <div>День</div>
              <div>Периодичность</div>
              <div>Первая</div>
              <div>Последняя</div>
            </div>

            <div
              v-for="day in weekdays"
              :key="day.value"
              class="day-schedule-row"
              :class="{ 'is-disabled': !form.scheduleDays[day.value].enabled }"
            >
              <v-checkbox
                v-model="form.scheduleDays[day.value].enabled"
                hide-details
                dense
                color="#4B5EFF"
                :label="day.label"
              />

              <v-select
                v-model="form.scheduleDays[day.value].frequency"
                :items="dayFrequencyOptions"
                item-text="label"
                item-value="value"
                dense
                outlined
                hide-details
                class="field"
                :disabled="!form.scheduleDays[day.value].enabled"
              />

              <v-text-field
                :value="form.scheduleDays[day.value].startTime"
                dense
                outlined
                hide-details
                class="time day-start-time"
                type="tel"
                inputmode="numeric"
                maxlength="5"
                :disabled="!form.scheduleDays[day.value].enabled"
                @input="(value) => setDayStartTime(day.value, value)"
                @blur="normalizeDayStartTime(day.value)"
              />

              <v-select
                v-if="form.scheduleDays[day.value].frequency !== 'once'"
                v-model="form.scheduleDays[day.value].endTime"
                :items="getDayEndTimes(day.value)"
                dense
                outlined
                hide-details
                class="time"
                :disabled="
                  !form.scheduleDays[day.value].enabled ||
                  !getDayEndTimes(day.value).length
                "
              />

              <div v-else class="day-empty">—</div>

              <div
                v-if="
                  form.scheduleDays[day.value].enabled &&
                  form.scheduleDays[day.value].frequency !== 'once' &&
                  form.scheduleDays[day.value].startTime &&
                  !getDayEndTimes(day.value).length
                "
                class="day-schedule-error"
              >
                Повторная автозамена в этот день невозможна. Выберите более
                раннее время или меньшую периодичность.
              </div>
            </div>
          </div>

          <div class="tz-hint">Время по МСК</div>
        </template>
      </template>

      <template v-else>
        <div class="form-control">
          <div class="field-label">Порог звонков с одного номера</div>
          <v-text-field
            v-model.number="form.autoCallsThreshold"
            type="number"
            min="10"
            max="99999"
            dense
            outlined
            hide-details
            @blur="normalizeThreshold"
          />
        </div>
        <div class="warning-text">
          Для этой автозамены срок аренды номеров сокращается до 3 дней.
        </div>
      </template>

      <div v-if="form.autoSource === 'reserve'" class="form-control">
        <div class="field-label">Резерв</div>
        <v-select
          v-model="form.autoReserveId"
          :items="reserveOptions"
          item-text="label"
          item-value="id"
          dense
          outlined
          hide-details
          :loading="loadingReserves"
          :disabled="loadingReserves"
          @focus="$emit('load-reserves')"
        />
      </div>
    </template>
  </div>
</template>

<script>
const WEEKDAYS = [
  { label: 'Пн', value: 'mon' },
  { label: 'Вт', value: 'tue' },
  { label: 'Ср', value: 'wed' },
  { label: 'Чт', value: 'thu' },
  { label: 'Пт', value: 'fri' },
  { label: 'Сб', value: 'sat' },
  { label: 'Вс', value: 'sun' },
];

function dayConfig(enabled) {
  return { enabled, frequency: 'once', startTime: '20:00', endTime: '' };
}

function defaults() {
  return {
    autorenew: false,
    autoreplace: false,
    autoReplaceType: 'schedule',
    autoCallsThreshold: 1000,
    scheduleSeparateDays: false,
    scheduleFrequency: 'once',
    scheduleStartTime: '20:00',
    scheduleEndTime: '',
    scheduleCustomHours: 0,
    scheduleCustomMinutes: 30,
    days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    scheduleDays: {
      mon: dayConfig(true),
      tue: dayConfig(true),
      wed: dayConfig(true),
      thu: dayConfig(true),
      fri: dayConfig(true),
      sat: dayConfig(false),
      sun: dayConfig(false),
    },
    autoSource: 'regular',
    autoReserveId: null,
    amount: 10,
  };
}

export default {
  name: 'CarouselAutoSettings',
  props: {
    value: { type: Object, default: () => ({}) },
    reserveOptions: { type: Array, default: () => [] },
    loadingReserves: { type: Boolean, default: false },
  },
  data() {
    return {
      syncing: false,
      form: this.mergeValue(this.value),
      weekdays: WEEKDAYS,
      frequencyOptions: [
        { label: 'Один раз в день', value: 'once' },
        { label: 'Каждые 30 минут', value: '30m' },
        { label: 'Каждый час', value: '1h' },
        { label: 'Каждые 2 часа', value: '2h' },
        { label: 'Другая периодичность', value: 'custom' },
      ],
      dayFrequencyOptions: [
        { label: 'Один раз в день', value: 'once' },
        { label: 'Каждые 30 минут', value: '30m' },
        { label: 'Каждый час', value: '1h' },
        { label: 'Каждые 2 часа', value: '2h' },
      ],
    };
  },
  computed: {
    selectedReserveLeft() {
      const reserve = this.reserveOptions.find(
        (item) => String(item.id) === String(this.form.autoReserveId),
      );
      return Math.max(0, Number(reserve && reserve.available) || 0);
    },
    customScheduleStepInvalid() {
      if (this.form.scheduleFrequency !== 'custom') return false;
      const minutes =
        (Number(this.form.scheduleCustomHours) || 0) * 60 +
        (Number(this.form.scheduleCustomMinutes) || 0);
      return minutes < 10 || minutes > 720;
    },
    availableEndTimes() {
      return this.buildEndTimes(
        this.form.scheduleStartTime,
        this.getStepMinutes(this.form.scheduleFrequency),
      );
    },
    schedulePreview() {
      if (!this.form.days?.length) {
        return 'Выберите хотя бы один день автозамены.';
      }

      if (this.form.scheduleFrequency === 'once') {
        return `В выбранные дни автозамена выполнится один раз: в ${this.form.scheduleStartTime}.`;
      }

      if (!this.form.scheduleEndTime) {
        return 'Выберите время последней автозамены.';
      }

      const times = [this.form.scheduleStartTime];
      const endIndex = this.availableEndTimes.indexOf(
        this.form.scheduleEndTime,
      );

      if (endIndex >= 0) {
        times.push(...this.availableEndTimes.slice(0, endIndex + 1));
      }

      return `В выбранные дни автозамена выполнится ${times.length} раз: ${times
        .slice(0, 3)
        .join(', ')}${
        times.length > 3 ? ` ... ${times[times.length - 1]}` : ''
      }.`;
    },
    payload() {
      const schedule = {
        mode: this.form.scheduleSeparateDays ? 'by_days' : 'base',
        frequency: this.form.scheduleFrequency,
        days: this.form.days,
        time:
          this.form.scheduleFrequency === 'once'
            ? this.form.scheduleStartTime
            : '',
        startTime: this.form.scheduleStartTime,
        endTime: this.form.scheduleEndTime,
        customHours: Number(this.form.scheduleCustomHours) || 0,
        customMinutes: Number(this.form.scheduleCustomMinutes) || 0,
        daysConfig: this.form.scheduleDays,
      };
      return {
        autorenew: !!this.form.autorenew,
        autoreplace: !!this.form.autoreplace,
        days: Array.isArray(this.form.days) ? this.form.days : [],
        time: this.form.scheduleStartTime,
        amount: Number(this.form.amount) || 0,
        auto_source: this.form.autoSource,
        auto_reserve_id: this.form.autoReserveId,
        auto_replace_type: this.form.autoReplaceType,
        auto_calls_threshold: Number(this.form.autoCallsThreshold) || 10,
        auto_schedule_json: JSON.stringify(schedule),
      };
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        if (this.syncing) return;
        this.form = this.mergeValue(value);
      },
    },
    form: {
      deep: true,
      handler() {
        this.syncing = true;
        this.$emit('input', { ...this.form });
        this.$emit('change', this.payload);
        this.$nextTick(() => {
          this.syncing = false;
        });
      },
    },
    'form.autorenew'(enabled) {
      if (enabled) this.form.autoreplace = false;
    },
    'form.autoreplace'(enabled) {
      if (enabled) this.form.autorenew = false;
    },
    'form.autoSource'(source) {
      if (source !== 'reserve') this.form.autoReserveId = null;
    },
    'form.autoReserveId'() {
      this.normalizeAmount();
    },
    'form.scheduleFrequency'() {
      this.form.scheduleEndTime = '';
    },
  },
  methods: {
    mergeValue(value) {
      const base = defaults();
      const source = value || {};
      return {
        ...base,
        ...source,
        days: Array.isArray(source.days) ? [...source.days] : base.days,
        scheduleDays: { ...base.scheduleDays, ...(source.scheduleDays || {}) },
      };
    },
    getStepMinutes(frequency) {
      if (frequency === '30m') return 30;
      if (frequency === '1h') return 60;
      if (frequency === '2h') return 120;
      if (frequency === 'custom')
        return (
          (Number(this.form.scheduleCustomHours) || 0) * 60 +
          (Number(this.form.scheduleCustomMinutes) || 0)
        );
      return 0;
    },
    buildEndTimes(start, step) {
      if (
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(start || '') ||
        step < 10 ||
        step > 720
      )
        return [];
      const parts = start.split(':').map(Number);
      const result = [];
      for (
        let value = parts[0] * 60 + parts[1] + step;
        value < 1440;
        value += step
      ) {
        result.push(
          `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(
            value % 60,
          ).padStart(2, '0')}`,
        );
      }
      return result;
    },
    getDayEndTimes(day) {
      const config = this.form.scheduleDays[day];
      return this.buildEndTimes(
        config.startTime,
        this.getStepMinutes(config.frequency),
      );
    },
    maskTime(value) {
      const digits = String(value || '')
        .replace(/\D/g, '')
        .slice(0, 4);
      return digits.length > 2
        ? `${digits.slice(0, 2)}:${digits.slice(2)}`
        : digits;
    },
    normalizeTime(value) {
      const match = String(value || '').match(/^(\d{1,2}):(\d{1,2})$/);
      if (!match) return '20:00';
      const hours = Math.min(23, Number(match[1]) || 0);
      const minutes = Math.min(59, Number(match[2]) || 0);
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
        2,
        '0',
      )}`;
    },
    setBaseStartTime(value) {
      this.form.scheduleStartTime = this.maskTime(value);
      this.form.scheduleEndTime = '';
    },
    normalizeBaseStartTime() {
      this.form.scheduleStartTime = this.normalizeTime(
        this.form.scheduleStartTime,
      );
    },
    setDayStartTime(day, value) {
      this.form.scheduleDays[day].startTime = this.maskTime(value);
      this.form.scheduleDays[day].endTime = '';
    },
    normalizeDayStartTime(day) {
      this.form.scheduleDays[day].startTime = this.normalizeTime(
        this.form.scheduleDays[day].startTime,
      );
    },
    normalizeThreshold() {
      this.form.autoCallsThreshold = Math.max(
        10,
        Math.min(99999, Number(this.form.autoCallsThreshold) || 10),
      );
    },
    normalizeAmount() {
      const max =
        this.form.autoSource === 'reserve' ? this.selectedReserveLeft : 10000;
      this.form.amount = Math.max(
        max > 0 ? 1 : 0,
        Math.min(max, Number(this.form.amount) || 0),
      );
    },
  },
  filters: {
    rub(value) {
      const number = Number(value) || 0;
      try {
        return new Intl.NumberFormat('ru-RU').format(number) + ' ₽';
      } catch (e) {
        return number + ' ₽';
      }
    },
  },
};
</script>

<style scoped>
.threshold-warning {
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff7df;
  color: #7a5200;
  font-size: 13px;
  line-height: 1.35;
}
.edit-carousel-dialog {
  overflow-y: hidden !important;
}

.edit-carousel-dialog .ecm.modal-card {
  max-height: calc(100vh - 64px);
  overflow-y: auto;
}

.edit-carousel-dialog .v-dialog {
  margin: 16px;
}

@media (max-width: 600px) {
  .edit-carousel-dialog .v-dialog {
    margin: 8px;
  }
  .edit-carousel-dialog .ecm.modal-card {
    max-height: calc(100vh - 32px);
  }
}
.ecm {
  --tz-text: #2b2d33;
  --tz-muted: #808185;
  --tz-line: #d9dade;
  --tz-bg: #f8f8f8;
  --tz-accent: #4b5eff;
}
.edit-carousel-dialog {
  overflow: visible;
}
.ecm.modal-card {
  padding: 32px 40px 40px;
  border-radius: 8px;
}
.ecm .modal-title {
  margin: 0 0 24px;
  font: 700 26px/1.3 Manrope, sans-serif;
  text-align: center;
  color: var(--tz-text);
}
.ecm .close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  min-width: 32px;
  height: 32px;
}
.ecm .close-btn .v-icon {
  font-size: 22px;
  color: #808185;
}
.ecm .form-control {
  margin-top: 20px;
}
.ecm .field-label,
.ecm .sub-label {
  text-align: left !important;
}
.ecm .field-label {
  font: 600 13px/1.1 Manrope, sans-serif;
  color: var(--tz-text);
  margin-bottom: 8px;
}
.ecm .field .v-input__slot {
  min-height: 44px;
  border-radius: 8px;
  border-color: var(--tz-line);
}
.ecm .field .v-label {
  display: none;
}
.ecm .field input,
.ecm .field .v-select__selections {
  font: 500 14px/1.2 Manrope, sans-serif;
  color: var(--tz-text);
}
.ecm .field .v-input--is-focused .v-input__slot,
.ecm .field .v-input__slot:hover {
  border-color: var(--tz-accent) !important;
}
.ecm .form-control + .checkbox {
  margin-top: 24px !important;
}
.ecm .checkbox + .checkbox {
  margin-top: 16px !important;
}
.ecm .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ecm .help-icon {
  color: #c8cad0 !important;
}
.ecm .schedule-row {
  display: grid;
  grid-template-columns: 1fr 168px;
  column-gap: 16px;
  align-items: start;
  margin-top: 24px;
}
.ecm .days-input {
  min-height: 40px;
  border: 1px solid var(--tz-line);
  border-radius: 8px;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.ecm .days {
  margin: 0;
  display: flex;
  flex-wrap: nowrap;
}
.ecm .days .v-chip {
  height: 28px;
  padding: 0 6px;
  font-size: 12px;
  line-height: 1;
  border-radius: 6px;
  margin-right: 6px;
  background: #f2f3f5;
  color: #6f7075;
  font-weight: 700;
}
.ecm .days .v-chip:last-child {
  margin-right: 0;
}
.ecm .days .v-chip.v-chip--active {
  background: var(--tz-accent) !important;
  color: #fff !important;
}
.ecm .time {
  margin: 0;
  width: 168px;
}
.ecm .time .v-input__slot {
  min-height: 44px;
  border-radius: 8px;
  border-color: var(--tz-line);
}
.ecm .time input {
  font-variant-numeric: tabular-nums;
}
.ecm .time .v-input--is-focused .v-input__slot,
.ecm .time .v-input__slot:hover {
  border-color: var(--tz-accent) !important;
}
.ecm .price-box {
  background: #fff;
  border: 1px solid var(--tz-line);
  border-radius: 8px;
  padding: 18px 22px;
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.ecm .price-label {
  font: 500 13px/1.2 Manrope, sans-serif;
  color: var(--tz-muted);
}
.ecm .price-value {
  font: 800 20px/1.3 Manrope, sans-serif;
  color: var(--tz-text);
}
.ecm .price-right {
  text-align: right;
}
.ecm .yellow-btn {
  background: var(--tz-accent) !important;
  color: #fff !important;
  font: 800 16px/1.2 Manrope, sans-serif;
  border-radius: 8px;
}
.ecm .checkbox-label {
  color: #2b2d33;
  font-weight: 600;
}
.ecm .help-icon {
  color: #9ea0a6 !important;
}
.ecm .tz-hint {
  margin-top: 6px;
  font: 600 11px/1.1 Manrope, sans-serif;
  color: #6f7075;
  white-space: nowrap;
  opacity: 0.9;
}

/* Резервы — оформление пунктов и выбранного значения */
.reserve-item {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  padding: 4px 0;
}
.reserve-item.is-empty {
  opacity: 0.6;
}
.reserve-title {
  font-weight: 700;
}
.reserve-meta {
  font-size: 12px;
  color: #7b7d83;
  display: flex;
  gap: 10px;
}
.reserve-selection {
  display: flex;
  align-items: center;
  gap: 6px;
}
.reserve-selection-mark {
  color: #7b7d83;
  font-weight: 600;
}
.muted {
  color: #808185;
}
.ecm .schedule-preview {
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.35;
  color: #6f7075;
}
.ecm .schedule-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 168px;
  gap: 16px;
  align-items: end;
  margin-top: 16px;
}

.ecm .schedule-preview {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e6e7eb;
  border-radius: 8px;
  background: #f8f9fb;
  font-size: 13px;
  line-height: 1.35;
  color: #5f626a;
}

.ecm .day-schedule-list {
  margin-top: 16px;
  border: 1px solid var(--tz-line);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.ecm .day-schedule-head,
.ecm .day-schedule-row {
  display: grid;
  grid-template-columns: 78px minmax(170px, 1fr) 120px 120px;
  gap: 10px;
  align-items: center;
}

.ecm .day-schedule-head {
  padding: 10px 12px;
  background: #f5f6f8;
  color: #6f7075;
  font-size: 12px;
  font-weight: 700;
}

.ecm .day-schedule-row {
  padding: 10px 12px;
  border-top: 1px solid #eceef2;
}

.ecm .day-schedule-row .v-input {
  margin-top: 0;
}

.ecm .day-schedule-row .v-input__slot {
  min-height: 38px !important;
}

.ecm .day-schedule-row .time {
  width: 116px;
}

.ecm .day-empty {
  height: 38px;
  display: flex;
  align-items: center;
  color: #9ea0a6;
  font-weight: 700;
}

@media (max-width: 600px) {
  .ecm.modal-card {
    padding: 24px 18px 28px;
  }

  .ecm .schedule-row {
    grid-template-columns: 1fr;
  }

  .ecm .time {
    width: 100%;
  }

  .ecm .day-schedule-list {
    border: 0;
    background: transparent;
  }

  .ecm .day-schedule-head {
    display: none;
  }

  .ecm .day-schedule-row {
    grid-template-columns: 1fr;
    border: 1px solid var(--tz-line);
    border-radius: 8px;
    margin-bottom: 10px;
    background: #fff;
  }

  .ecm .day-schedule-row .time {
    width: 100%;
  }
}
.ecm .day-schedule-row.is-disabled {
  background: #fafafb;
}

.ecm .day-schedule-row.is-disabled .v-input,
.ecm .day-schedule-row.is-disabled .day-empty {
  opacity: 0.65;
}
.ecm .schedule-error {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff1f1;
  color: #c62828;
  font-size: 13px;
  line-height: 1.35;
}
.ecm .day-schedule-error {
  grid-column: 2 / -1;
  margin-top: -4px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff1f1;
  color: #c62828;
  font-size: 12px;
  line-height: 1.3;
}
.ecm .sub-label {
  font: 600 13px/1.25 Manrope, sans-serif;
  color: var(--tz-text);
  margin-bottom: 8px;
}
.ecm .day-schedule-row .day-start-time .v-input__slot {
  min-height: 58px !important;
  height: 58px !important;
}
.ecm .schedule-error--compact {
  margin-top: 8px;
}
.ecm.carousel-auto-settings {
  display: block;
  margin-top: 20px;
  margin-bottom: 20px;
}

.ecm .day-row {
  display: grid;
  grid-template-columns: 78px minmax(170px, 1fr) 120px 120px;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-top: 1px solid #eceef2;
}

.ecm .day-schedule-list .day-row:first-child {
  border-top: 0;
}

.ecm .warning-text {
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff7df;
  color: #7a5200;
  font-size: 13px;
  line-height: 1.35;
}

.ecm .error-text {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff1f1;
  color: #c62828;
  font-size: 13px;
}
.ecm .separate-days-checkbox {
  margin-top: 18px !important;
}

.ecm .schedule-row .time .v-input__slot {
  min-height: 44px !important;
  height: 44px !important;
}

.ecm .schedule-row .time.v-select .v-select__slot {
  height: 100%;
  align-items: center;
}

.ecm .schedule-row .time.v-select .v-select__selections {
  min-height: 0 !important;
  height: auto !important;
  padding: 0 !important;
  align-items: center;
}

.ecm .schedule-row .time.v-select .v-select__selection {
  margin: 0 4px 0 0 !important;
  line-height: 1.2 !important;
}

.ecm .schedule-row .time.v-select .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}
.ecm .schedule-row .time .v-select__selection {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
.ecm .day-row .day-field .v-input__slot {
  min-height: 44px !important;
  height: 44px !important;
}

.ecm .day-row .day-field.v-select .v-select__slot {
  height: 100%;
  align-items: center;
}

.ecm .day-row .day-field.v-select .v-select__selections {
  min-height: 0 !important;
  height: auto !important;
  padding: 0 !important;
  align-items: center;
}

.ecm .day-row .day-field.v-select .v-select__selection {
  margin: 0 4px 0 0 !important;
  line-height: 1.2 !important;
}

.ecm .day-row .day-field.v-select .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}
.carousel-auto-settings /deep/ .custom-period-field .v-input__slot {
  min-height: 50px !important;
  height: 50px !important;
}

.carousel-auto-settings /deep/ .custom-period-field input {
  padding-top: 16px !important;
  padding-bottom: 4px !important;
}
</style>
